import {
  CreateStepOptions,
  FlatStep,
  MappedStep,
  UseStepperResult,
} from "@/utils/stepper";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, ChevronLeftIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "./generic/Button";
import { StickyElement } from "./layout/StickyElement";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Step<TStep extends CreateStepOptions<any, string>> =
  | FlatStep<TStep>
  | MappedStep<TStep>;

export function StepperSteps<
  TContext,
  TStepId extends string,
  TStep extends CreateStepOptions<TContext, TStepId>,
>({
  stepper,
  ...props
}: {
  stepper: UseStepperResult<TContext, TStepId, TStep>;
  steps?: Step<TStep>[];
  children?: (context: {
    steps: {
      step: Step<TStep>;
      stepNumber: number;
      isActiveStep: boolean;
      isBeforeActiveStep: boolean;
      prevStep: Step<TStep> | null;
      nextStep: Step<TStep> | null;
    }[];
  }) => React.ReactNode;
}) {
  const steps = props.steps ?? stepper.visibleSteps;

  const activeLocalStep = useMemo(() => {
    const localStepsSet = new Set(steps);
    for (const step of [...stepper.activeFlatSteps].reverse()) {
      if (localStepsSet.has(step)) return step;
    }
    return null;
  }, [stepper.activeFlatSteps, steps]);

  const activeLocalStepIndex = useMemo(
    () =>
      activeLocalStep
        ? steps.findIndex((step) => step === activeLocalStep)
        : -1,
    [activeLocalStep, steps],
  );

  return props.children?.({
    steps: steps.map((step, i) => {
      const prevStep = i ? steps[i - 1] : null;
      const nextStep = i < steps.length - 1 ? steps[i + 1] : null;

      return {
        step,
        stepNumber: i + 1,
        isActiveStep: step === activeLocalStep,
        isBeforeActiveStep: i < activeLocalStepIndex,
        prevStep,
        nextStep,
      };
    }),
  });
}

export function VerticalStepperSteps<
  TContext,
  TStepId extends string,
  TStep extends CreateStepOptions<TContext, TStepId>,
>({
  stepper,
  steps,
}: {
  stepper: UseStepperResult<TContext, TStepId, TStep>;
  steps?: Step<TStep>[];
}) {
  return (
    <StepperSteps stepper={stepper} steps={steps}>
      {({ steps }) =>
        steps.map(
          ({ step, stepNumber, isBeforeActiveStep, prevStep, nextStep }) => {
            const renderStepLine = (enabled: boolean) => (
              <div className="relative h-full w-full">
                <div
                  className={twMerge(
                    "border-theme-outline-variant absolute inset-y-0 left-1/2 -translate-x-1/2 border-l transition-colors",
                    enabled && "border-theme-gradient-to",
                  )}
                />
              </div>
            );

            return (
              <React.Fragment key={step.id}>
                {prevStep && (
                  <div className="h-10 w-8">
                    {renderStepLine(step.canBeAccessed)}
                  </div>
                )}

                <Link
                  className="-m-2 p-2"
                  to={step.to}
                  disabled={!step.canBeAccessed}
                >
                  {({ isActive }) => (
                    <div className="flex items-center gap-x-3">
                      <div className="flex flex-col items-center justify-center self-stretch">
                        <div className="h-0 grow">
                          {prevStep && renderStepLine(step.canBeAccessed)}
                        </div>

                        <div
                          className={twMerge(
                            "grid size-8 place-items-center rounded-full border-2 text-[12px] font-semibold transition-colors *:col-start-1 *:row-start-1",
                            isActive
                              ? "text-theme-gradient-to bg-theme-gradient-to/30 border-theme-gradient-to/40"
                              : step.canBeAccessed
                                ? "text-theme-gradient-to border-theme-gradient-to"
                                : "text-theme-on-background-variant bg-theme-outline-variant border-theme-outline-variant",
                          )}
                        >
                          <AnimatePresence initial={false}>
                            {isBeforeActiveStep &&
                            step.canBeAccessed &&
                            step.isValid ? (
                              <motion.div
                                key="check"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                              >
                                <CheckIcon size={16} />
                              </motion.div>
                            ) : (
                              <motion.div
                                key="number"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                              >
                                {stepNumber}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        <div className="h-0 grow">
                          {nextStep && renderStepLine(nextStep.canBeAccessed)}
                        </div>
                      </div>

                      <div
                        className={twMerge(
                          "flex flex-col gap-y-1 transition-opacity",
                          !step.canBeAccessed && "opacity-50",
                        )}
                      >
                        <div
                          className={twMerge(
                            "text-[12px] font-semibold transition-colors",
                            !step.canBeAccessed &&
                              "text-theme-on-background-variant",
                            isActive && "text-theme-active",
                          )}
                        >
                          {step.name}
                        </div>

                        <div className="text-theme-on-background-variant text-[10px] font-semibold">
                          {step.description}
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              </React.Fragment>
            );
          },
        )
      }
    </StepperSteps>
  );
}

export function HorizontalStepperSteps<
  TContext,
  TStepId extends string,
  TStep extends CreateStepOptions<TContext, TStepId>,
>({
  stepper,
  steps,
}: {
  stepper: UseStepperResult<TContext, TStepId, TStep>;
  steps?: Step<TStep>[];
}) {
  return (
    <StepperSteps stepper={stepper} steps={steps}>
      {({ steps }) => (
        <AnimatePresence initial={false}>
          {steps.flatMap(
            ({ step, stepNumber, isBeforeActiveStep, prevStep }) => {
              return [
                prevStep && (
                  <motion.div
                    key={`step-line-${stepNumber}`}
                    className="h-full"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 40, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                  >
                    <div className="size-full px-2">
                      <div className="relative size-full">
                        <div
                          className={twMerge(
                            "border-theme-outline-variant absolute inset-x-0 top-1/2 -translate-y-1/2 border-t transition-colors",
                            step.canBeAccessed && "border-theme-gradient-to",
                          )}
                        />
                      </div>
                    </div>
                  </motion.div>
                ),

                <motion.div
                  key={`step-${stepNumber}`}
                  className="flex items-center justify-center"
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "auto", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                >
                  <div className="-m-2">
                    <Link
                      className="flex w-full shrink p-2"
                      to={step.to}
                      disabled={!step.canBeAccessed}
                    >
                      {({ isActive }) => (
                        <div className="flex w-full items-center">
                          <div
                            className={twMerge(
                              "grid size-5 shrink-0 grow-0 place-items-center rounded-full border text-[11px] font-medium transition-colors *:col-start-1 *:row-start-1",
                              isActive
                                ? "text-theme-gradient-to bg-theme-gradient-to/30 border-theme-gradient-to/40"
                                : step.canBeAccessed
                                  ? "text-theme-gradient-to border-theme-gradient-to"
                                  : "text-theme-on-background-variant bg-theme-outline-variant border-theme-outline-variant",
                            )}
                          >
                            <AnimatePresence initial={false}>
                              {isBeforeActiveStep &&
                              step.canBeAccessed &&
                              step.isValid ? (
                                <motion.div
                                  key="check"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                >
                                  <CheckIcon size={12} />
                                </motion.div>
                              ) : (
                                <motion.div
                                  key="number"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                >
                                  {stepNumber}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          <div
                            className={twMerge(
                              "flex min-w-0 shrink overflow-hidden text-[12px] font-semibold transition-[color,opacity]",
                              !step.canBeAccessed &&
                                "text-theme-on-background-variant opacity-50",
                              isActive && "text-theme-active",
                            )}
                          >
                            <div className="pl-2">
                              <TransitioningText>{step.name}</TransitioningText>
                            </div>
                          </div>
                        </div>
                      )}
                    </Link>
                  </div>
                </motion.div>,
              ];
            },
          )}
        </AnimatePresence>
      )}
    </StepperSteps>
  );
}

export function StepperBottomBar<
  TContext,
  TStepId extends string,
  TStep extends CreateStepOptions<TContext, TStepId>,
>({
  stepper,
  hideSubmitButton = false,
  lastStepSubmitText,
  slots,
}: {
  stepper: UseStepperResult<TContext, TStepId, TStep>;
  hideSubmitButton?: boolean;
  lastStepSubmitText?: string;
  slots?: {
    beforeBackButton?: () => React.ReactNode;
    afterBackButton?: () => React.ReactNode;
    beforeSubmitButton?: () => React.ReactNode;
    afterSubmitButton?: () => React.ReactNode;
  };
}) {
  return (
    <StickyElement
      className="shrink-0"
      stickyProps={{ className: "bg-theme-background" }}
      side="bottom"
    >
      <div className="shrink-0 px-8 xl:px-14">
        <div className="border-t-theme-outline-variant border-t" />

        <div className="flex items-center justify-between gap-x-4 py-9">
          <div className="flex items-center gap-x-4">
            {slots?.beforeBackButton?.()}

            <button
              className={twMerge(
                "text-theme-on-background hover:text-theme-on-background/90 -mx-4 flex cursor-pointer items-center justify-center gap-x-2 rounded-xs px-4 py-2 transition-opacity focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-500",
                !stepper.canGoPrev && "cursor-default opacity-0",
              )}
              disabled={!stepper.canGoPrev}
              onClick={() => {
                stepper.goPrev();
              }}
            >
              <ChevronLeftIcon className="-mx-1.5 size-5" />

              <span className="text-sm/[16px] font-medium uppercase">
                Tilbage
              </span>
            </button>

            {slots?.afterBackButton?.()}
          </div>

          <div className="flex items-center gap-x-4">
            {slots?.beforeSubmitButton?.()}

            {!hideSubmitButton && (
              <Button
                isDisabled={!stepper.canSubmitOrGoNext}
                isPending={stepper.isSubmitting}
                onPress={stepper.submitOrGoNext}
              >
                {(!stepper.nextStep && lastStepSubmitText) ||
                  (stepper.nextIsSubmit
                    ? (stepper.submitText ?? "Næste")
                    : "Næste")}
              </Button>
            )}

            {slots?.afterSubmitButton?.()}
          </div>
        </div>
      </div>
    </StickyElement>
  );
}

function TransitioningText({
  className,
  children,
}: {
  className?: string;
  children: string;
}) {
  const [width, setWidth] = useState<number | "auto">("auto");
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!containerEl) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // We only have one entry, so we can use entries[0].
      const observedWidth = entries?.[0]?.contentRect?.width;
      setWidth(observedWidth ?? "auto");
    });

    resizeObserver.observe(containerEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerEl]);

  return (
    <motion.div
      className={twMerge("relative overflow-hidden text-nowrap", className)}
      animate={{ width }}
    >
      <div
        ref={setContainerEl}
        className="invisible absolute right-full bottom-full"
      >
        {children}
      </div>

      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={children}
          variants={{
            active: {
              opacity: 1,
            },
            inactive: {
              opacity: 0,
            },
          }}
          initial="inactive"
          animate="active"
          exit="inactive"
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
